import { useCallback, useEffect, useRef, useState } from 'react';
import { OperationResult, useClient } from 'urql';

import {
  FiltersStringOps,
  GetCollectionItemsDocument,
  GetCollectionItemsQuery,
} from '@/graphql/types/generated';

const PAGE_SIZE = 100;

type ItemEdge = NonNullable<
  GetCollectionItemsQuery['viewer']
>['availableItems']['edges'][number];

export function useAllCollectionItems(itemIds: string[]) {
  const client = useClient();
  const abortRef = useRef(0);
  const [fetching, setFetching] = useState(false);
  const [edges, setEdges] = useState<ItemEdge[]>([]);

  const fetchAll = useCallback(async () => {
    if (itemIds.length === 0) {
      setEdges([]);
      return;
    }

    const fetchId = ++abortRef.current;
    setFetching(true);

    const allEdges: ItemEdge[] = [];
    let after: string | null = null;
    let hasMore = true;

    while (hasMore) {
      const result: OperationResult<GetCollectionItemsQuery> = await client
        .query<GetCollectionItemsQuery>(
          GetCollectionItemsDocument,
          {
            after,
            first: PAGE_SIZE,
            filters: {
              id: { op: FiltersStringOps.Contain, value: itemIds },
            },
          },
          { requestPolicy: 'cache-and-network' },
        )
        .toPromise();

      if (fetchId !== abortRef.current) {
        return;
      }

      const connection = result.data?.viewer?.availableItems;
      if (connection?.edges) {
        allEdges.push(...connection.edges);
      }

      hasMore = connection?.pageInfo?.hasNextPage ?? false;
      after = connection?.pageInfo?.endCursor ?? null;
    }

    setEdges(allEdges);
    setFetching(false);
  }, [client, itemIds]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { edges, fetching };
}
